import { useMemo } from "react";
import { useGetInvoicesByStudentIdQuery } from "../api/invoiceApi";
import { Invoice } from "../types/invoiceTypes";
import { format } from "date-fns";

export interface StudentInvoiceSummary {
  totalAmount: number;
  totalPayable: number;
  paidAmount: number;
  pendingAmount: number;
  invoiceCount: number;
  paidInvoiceCount: number;
  pendingInvoiceCount: number;
  totalDiscount: number;
}

export const useStudentInvoices = (studentId: string) => {
  const {
    data: invoicesResponse,
    isLoading,
    error,
    refetch,
  } = useGetInvoicesByStudentIdQuery(studentId);

  const invoices = useMemo(() => {
    return invoicesResponse?.data?.invoices || [];
  }, [invoicesResponse]);

  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), "dd MMM yyyy");
    } catch (e) {
      return dateString;
    }
  };

  // Get invoice by ID
  const getInvoiceById = (invoiceId: number): Invoice | undefined => {
    return invoices.find((invoice) => invoice.id === invoiceId);
  };

  // Transform invoices to a format suitable for the UI
  const invoiceRows = useMemo(() => {
    return (
      invoices
        .map((invoice) => {
          const paymentDate = "-";
          const paymentMode = "-";

          const discountValue = invoice.discount_value;
          const discountAmount =
            invoice.discount_type === "percent"
              ? parseFloat(invoice.amount) * ((discountValue as any) / 100)
              : discountValue;

          // Calculate the status based on student_payments
          let status = "pending";
          if (invoice.student_payments && invoice.student_payments.length > 0) {
            const totalPaid = invoice.student_payments.reduce(
              (sum, payment) => 
                payment.payment_status === "completed" ? sum + payment.amount : sum, 
              0
            );
            
            if (totalPaid >= parseFloat(invoice.payable)) {
              status = "completed";
            } else if (totalPaid > 0) {
              status = "partial";
            }
          }

          return {
            id: invoice.id,
            stageId: invoice.stage_id,
            stageName:
              invoice.type === "registrationfee"
                ? "Registration"
                : invoice.stage?.name || "N/A",
            type: invoice.type || "coursefee",
            dueDate: formatDate(invoice.due_date),
            amount: parseFloat(invoice.amount),
            student_payments: invoice.student_payments || [],
            status: status,
            paymentDate: paymentDate,
            paymentMode: paymentMode,
            reference: `INV-${invoice.id}`,
            courseName: invoice.course.name,
            invoiceDate: formatDate(invoice.invoice_date),
            discount: discountAmount,
            payable: parseFloat(invoice.payable),
          };
        })
        // Sort the invoice rows to show registration fees at the top
        .sort((a, b) => {
          if (a.type === "registrationfee" && b.type !== "registrationfee")
            return -1;
          if (a.type !== "registrationfee" && b.type === "registrationfee")
            return 1;
          return 0;
        })
    );
  }, [invoices]);

  // Calculate invoice summary with correct paid amounts
  const summary: StudentInvoiceSummary = useMemo(() => {
    let totalAmount = 0;
    let totalPayable = 0;
    let paidAmount = 0;
    let pendingAmount = 0;
    let totalDiscount = 0;
    let paidInvoiceCount = 0;
    let pendingInvoiceCount = 0;

    invoiceRows.forEach((row) => {
      totalAmount += Number(row.amount);
      totalPayable += Number(row.payable);
      totalDiscount += Number(row.discount || 0);

      // Calculate paid amount from student_payments
      const rowPaidAmount = row.student_payments.reduce(
        (sum, payment) =>
          payment.payment_status === "completed" ? sum + payment.amount : sum, 
        0
      );

      paidAmount += rowPaidAmount;
      
      if (row.status === "completed") {
        paidInvoiceCount++;
      } else {
        pendingInvoiceCount++;
      }
    });

    pendingAmount = totalPayable - paidAmount;

    return {
      totalAmount,
      totalPayable,
      paidAmount,
      pendingAmount,
      invoiceCount: invoiceRows.length,
      paidInvoiceCount,
      pendingInvoiceCount,
      totalDiscount,
    };
  }, [invoiceRows]);

  return {
    invoices,
    invoiceRows,
    isLoading,
    error,
    refetch,
    summary,
    formatDate,
    getInvoiceById,
  };
};
